import React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import useUser from '../global/useUser';
import { searchItems } from '../../services/items';
import useDebouncedValue from '../useDebouncedValue';

interface Options {
  orderByFavoritedAt?: boolean;
  includeDeleted?: boolean;
  includeGiftCards?: boolean;
  giftCardsOnly?: boolean;
  enabled?: boolean;
}

function useSearchItems(
  query: string,
  {
    orderByFavoritedAt = true,
    includeDeleted = false,
    includeGiftCards = true,
    giftCardsOnly = false,
    enabled = true,
  }: Options = {},
) {
  const { user } = useUser();

  const debouncedQuery = useDebouncedValue(query, 500);
  const lowercasedQuery = debouncedQuery.trim().toLowerCase();

  const queryKey = React.useMemo(
    () => [
      'seach-retail-items',
      user?.business?.id,
      user?.role,
      includeDeleted,
      includeGiftCards,
      giftCardsOnly,
      lowercasedQuery,
      orderByFavoritedAt,
    ],
    [
      user?.business?.id,
      user?.role,
      includeDeleted,
      includeGiftCards,
      giftCardsOnly,
      lowercasedQuery,
      orderByFavoritedAt,
    ],
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: async ({ pageParam: cursor }) => {
      const result = await searchItems({
        businessId: user?.business?.id!,
        includeDeleted,
        includeGiftCards,
        giftCardsOnly,
        query: lowercasedQuery,
        cursor,
        orderByFavoritedAt,
      });

      return result;
    },
    enabled,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => {
      if (
        !lastPage.meta?.hasMore ||
        !lastPage.meta?.cursor ||
        !lastPage.meta?.count
      )
        return null;
      return lastPage.meta.cursor;
    },
  });

  const items = React.useMemo(
    () => data?.pages?.flatMap(page => page.result) ?? [],
    [data?.pages],
  );

  const totalCount = React.useMemo(
    () => (data?.pages ? data.pages[data.pages.length - 1].meta.count : 0),
    [data?.pages],
  );

  return {
    queryKey,
    data,
    totalCount,
    items,
    isLoading,
    isFetching,
    isDebouncing: query !== debouncedQuery,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
  };
}

export default useSearchItems;
